import { useEffect, useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { useConsoleApi } from "./useConsoleApi";
import { SEVERITIES, type CaFindingsResp } from "./types";
import { SEV_STYLE, SeverityTag, fmtDateTime, fmtNum, timeAgo } from "./utils";
import type { FindingsFilter } from "./ConsoleAnalyzerPage";

const PAGE_SIZE = 100;
const ALL = '__all__';


export function EmptyState({ children }: { children: React.ReactNode }) {
    return (
        <div className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">
            {children}
        </div>
    );
}


export default function FindingsTab({ bootId, refreshMs, now, filter, setFilter }: {
    bootId: string;
    refreshMs: number;
    now: number;
    filter: FindingsFilter;
    setFilter: (f: FindingsFilter) => void;
}) {
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [limit, setLimit] = useState(PAGE_SIZE);
    useEffect(() => {
        const id = setTimeout(() => setDebouncedSearch(search.trim()), 300);
        return () => clearTimeout(id);
    }, [search]);
    useEffect(() => setLimit(PAGE_SIZE), [bootId, filter.severity, filter.resource, debouncedSearch]);

    const resp = useConsoleApi<CaFindingsResp>('findings', {
        boot: bootId,
        severity: filter.severity,
        resource: filter.resource,
        search: debouncedSearch,
        limit,
    }, refreshMs);
    const data = resp.data;
    const resources = data?.resources ?? [];
    const hasFilter = !!(filter.severity || filter.resource || debouncedSearch);

    return (
        <div className="flex flex-col gap-3">
            {/* Filters */}
            <div className="flex flex-wrap items-center gap-2">
                <div className="flex flex-wrap gap-1">
                    <button
                        onClick={() => setFilter({ ...filter, severity: undefined })}
                        className={cn(
                            'rounded-sm border px-2 py-1 text-xs font-semibold uppercase tracking-wider',
                            !filter.severity ? 'bg-secondary text-secondary-foreground' : 'text-muted-foreground hover:bg-muted',
                        )}
                    >
                        All
                    </button>
                    {SEVERITIES.map((sev) => (
                        <button
                            key={sev}
                            onClick={() => setFilter({ ...filter, severity: filter.severity === sev ? undefined : sev })}
                            className={cn(
                                'rounded-sm border px-2 py-1 text-xs font-semibold uppercase tracking-wider',
                                filter.severity === sev
                                    ? cn(SEV_STYLE[sev].bg, SEV_STYLE[sev].text, SEV_STYLE[sev].border)
                                    : 'text-muted-foreground hover:bg-muted',
                            )}
                        >
                            {sev}
                        </button>
                    ))}
                </div>
                <Select
                    value={filter.resource ?? ALL}
                    onValueChange={(v) => setFilter({ ...filter, resource: v === ALL ? undefined : v })}
                >
                    <SelectTrigger className="h-8 w-56">
                        <SelectValue placeholder="All resources" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value={ALL}>All resources</SelectItem>
                        {filter.resource && !resources.includes(filter.resource) && (
                            <SelectItem value={filter.resource}>{filter.resource}</SelectItem>
                        )}
                        {resources.map((r) => (
                            <SelectItem key={r} value={r} className="font-mono text-xs">{r}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                <Input
                    className="h-8 w-60"
                    placeholder="Search message…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                />
                {hasFilter && (
                    <Button size="sm" variant="ghost" className="h-8" onClick={() => { setFilter({}); setSearch(''); }}>
                        Clear filters
                    </Button>
                )}
                <span className="ml-auto text-xs text-muted-foreground">
                    {data ? `${fmtNum(Math.min(data.items.length, data.total))} of ${fmtNum(data.total)} findings` : ''}
                </span>
            </div>

            {/* List */}
            {resp.error ? (
                <EmptyState>Failed to load findings: {(resp.error as Error).message}</EmptyState>
            ) : !data ? (
                <EmptyState>Loading…</EmptyState>
            ) : !data.items.length ? (
                <EmptyState>{hasFilter ? 'No finding matches these filters.' : 'No finding for this boot. FXServer did not complain about anything.'}</EmptyState>
            ) : (
                <div className="overflow-hidden rounded-lg border bg-card">
                    <div className="hidden grid-cols-[6rem_1fr_12rem_4.5rem_7rem_7rem] gap-3 border-b px-3 py-2 text-2xs font-semibold uppercase tracking-wider text-muted-foreground md:grid">
                        <span>Severity</span>
                        <span>Finding</span>
                        <span>Resource</span>
                        <span className="text-right">Count</span>
                        <span>First seen</span>
                        <span>Last seen</span>
                    </div>
                    {data.items.map((f) => (
                        <div
                            key={f.key}
                            className={cn(
                                'grid grid-cols-1 gap-x-3 gap-y-1 border-b border-l-2 px-3 py-2 last:border-b-0 hover:bg-muted/30 md:grid-cols-[6rem_1fr_12rem_4.5rem_7rem_7rem] md:items-start',
                                SEV_STYLE[f.severity].border,
                            )}
                        >
                            <div><SeverityTag severity={f.severity} /></div>
                            <div className="min-w-0">
                                <div className="text-sm font-medium">
                                    {f.title}
                                    {f.phase === 'boot' && (
                                        <span className="ml-2 text-2xs font-normal uppercase tracking-wider text-muted-foreground">at boot</span>
                                    )}
                                </div>
                                <div className="break-words font-mono text-xs text-muted-foreground">{f.message}</div>
                            </div>
                            <div className="min-w-0">
                                {f.resource ? (
                                    <button
                                        className="max-w-full truncate font-mono text-xs hover:underline"
                                        title={f.resource}
                                        onClick={() => setFilter({ ...filter, resource: f.resource! })}
                                    >
                                        {f.resource}
                                    </button>
                                ) : (
                                    <span className="text-xs italic text-muted-foreground">server</span>
                                )}
                            </div>
                            <div className="font-mono text-sm md:text-right">×{fmtNum(f.count)}</div>
                            <div className="text-xs text-muted-foreground" title={fmtDateTime(f.firstSeen)}>{timeAgo(f.firstSeen, now)}</div>
                            <div className="text-xs text-muted-foreground" title={fmtDateTime(f.lastSeen)}>{timeAgo(f.lastSeen, now)}</div>
                        </div>
                    ))}
                </div>
            )}
            {data && data.total > data.items.length && (
                <div className="flex justify-center">
                    <Button size="sm" variant="outline" onClick={() => setLimit(limit + PAGE_SIZE)}>
                        Load more ({fmtNum(data.total - data.items.length)} remaining)
                    </Button>
                </div>
            )}
        </div>
    );
}
